// connect/walk.ts — connecting a tool, walked by netti in its own rail.
//
// John, 2026-10-27: "the whole onboarding experience lives within netti, even the
// connection stage." Press connect on any tool and netti's rail opens and walks it
// there, in the conversation: what to paste and where (folded copy boxes), a live
// check that hears the tool's first call, the standing instruction that makes the
// tool reach for the memory on its own, letting it work without asking, bringing
// what it knows, and then what to connect next. Anything typed is answered: the
// words below are read first (they need no model and work for everyone the walk is
// open to), and what they do not cover goes to netti itself, with where the person
// is in the walk riding along (connectContextLine).
//
// This module is the walk's MODEL and WORDS, pure: no React, no fetch, no storage.
//   · a Walk is one tool being connected: its connection row, its name, its step.
//   · stepsFor(tool) is the order of the steps that tool has (only the true ones).
//   · readIntent(text) is what a typed line asks of the walk, or null.
//   · lines(...) are what netti says, in its voice: lowercase, direct, no dashes.
// Every step's facts come from lib/connect/tools (CONNECT_TOOLS) and lib/connect/
// setup (caveats, self-wiring), the one recorded log; nothing here restates a path
// that lives there.

import { CONNECT_TOOLS, MCP_URL, PLUGIN_CONNECT, connectTool, withPlugin, type ConnectTool, type GuideShot } from './tools'
import { CLIENT_CAVEATS, selfWire, wiresItself } from './setup'
import { connectTokens, namedClient } from './resolve'

// ── the walk ────────────────────────────────────────────────────────────────

/** a tool as the walk walks it. The walk runs only behind connect with netti (F.nettiConnect), and
 *  behind that flag ChatGPT and Codex are connected through their published plugin (tools withPlugin),
 *  so every tool the walk reaches for comes through here, never connectTool alone. */
export function walkTool(id: string): ConnectTool | null {
  const t = connectTool(id)
  return t ? withPlugin(t) : null
}

/** the steps of one walk, in the order they are walked. A walk on a connection that already has a
 *  name begins at wire. */
export type WalkStep = 'name' | 'wire' | 'instructions' | 'trust' | 'import' | 'done'
export const WALK_STEPS: readonly WalkStep[] = ['name', 'wire', 'instructions', 'trust', 'import', 'done']

/** how a tool is wired in: its published plugin (one press), one command on this machine, the browser
 *  extension doing it in the app's own page, or by hand, one step at a time */
export type WalkWay = 'plugin' | 'npx' | 'coach' | 'hand'
export const WALK_WAYS: readonly WalkWay[] = ['plugin', 'npx', 'coach', 'hand']

/** the word each step goes by in the card's step line */
export const STEP_WORD: Record<WalkStep, string> = {
  name: 'name',
  wire: 'connect',
  instructions: 'instruction',
  trust: 'allow',
  import: 'bring in',
  done: 'done',
}

export interface Walk {
  /** stable id, `w_…` */
  id: string
  /** the CONNECT_TOOLS id */
  tool: string
  /** the connection row this walk wires, once one is made or taken up */
  conn: string | null
  /** what the connection is called */
  label: string
  step: WalkStep
  /** how it is being wired, once chosen (or the only way there is) */
  way: WalkWay | null
  /** by hand: the step of the tool's own steps the person is on, 0-based */
  at: number
  /** when its first call landed (ISO), once heard */
  arrived: string | null
  /** the instruction check: the connection's call count when the person said it was pasted */
  heardFrom: number | null
  /** when a call after the instruction was pasted landed (ISO) */
  heard: string | null
  /** steps passed over */
  skipped: WalkStep[]
  /** this walk made its connection row (so switching away may let the unused one go) */
  made?: boolean
  /** where the press came from (list · orbit · netti · ask · tracker) */
  from: string
  started: number
  touched: number
}

export const WALK_CAP = 12

/** a fresh walk id */
export function walkId(now = Date.now()): string {
  return `w_${now.toString(36)}${Math.random().toString(36).slice(2, 6)}`
}

/** does this tool keep an export worth bringing in? (Claude Code says it keeps none) */
export function canImport(t: Pick<ConnectTool, 'importSteps'>): boolean {
  const s = t.importSteps ?? []
  return s.length > 0 && !/keeps no export/i.test(s[0] ?? '')
}

/** the steps this tool really has, in order. wire and done always; the rest only where true. */
export function stepsFor(t: ConnectTool): WalkStep[] {
  const out: WalkStep[] = ['name', 'wire', 'instructions']
  if (t.trust?.length) out.push('trust')
  if (canImport(t)) out.push('import')
  out.push('done')
  return out
}

/** the step after this one for this tool (done stays done) */
export function nextStep(t: ConnectTool, step: WalkStep): WalkStep {
  const steps = stepsFor(t)
  const i = steps.indexOf(step)
  return i < 0 || i >= steps.length - 1 ? 'done' : steps[i + 1]
}

/** where this step sits in the walk, 1-based, and how many steps there are before done */
export function stepIndex(t: ConnectTool, step: WalkStep): { at: number; of: number } {
  const steps = stepsFor(t).filter((s) => s !== 'done' && s !== 'name')
  const i = (steps as WalkStep[]).indexOf(step)
  return { at: i < 0 ? steps.length : i + 1, of: steps.length }
}

/** a walk read back from storage, only if every field is what it should be */
export function cleanWalk(raw: unknown): Walk | null {
  if (!raw || typeof raw !== 'object') return null
  const w = raw as Record<string, unknown>
  const tool = typeof w.tool === 'string' ? connectTool(w.tool) : null
  if (typeof w.id !== 'string' || !/^w_[a-z0-9]{2,24}$/.test(w.id) || !tool) return null
  const step = WALK_STEPS.includes(w.step as WalkStep) ? (w.step as WalkStep) : 'wire'
  const way = WALK_WAYS.includes(w.way as WalkWay) ? (w.way as WalkWay) : null
  const iso = (v: unknown) => (typeof v === 'string' && Number.isFinite(Date.parse(v)) ? v : null)
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)
  return {
    id: w.id,
    tool: tool.id,
    conn: typeof w.conn === 'string' && w.conn.length <= 64 ? w.conn : null,
    label: typeof w.label === 'string' ? w.label.slice(0, 80) : tool.name,
    step,
    way,
    at: Math.max(0, Math.min(12, Math.floor(num(w.at, 0)))),
    arrived: iso(w.arrived),
    heardFrom: typeof w.heardFrom === 'number' && Number.isFinite(w.heardFrom) ? w.heardFrom : null,
    heard: iso(w.heard),
    skipped: Array.isArray(w.skipped) ? (w.skipped.filter((s) => WALK_STEPS.includes(s as WalkStep)) as WalkStep[]) : [],
    ...(w.made === true ? { made: true } : {}),
    from: typeof w.from === 'string' ? w.from.slice(0, 24) : 'list',
    started: num(w.started, 0),
    touched: num(w.touched, 0),
  }
}

/** the walks worth keeping: the newest first, a tool walked once (its newest walk), capped */
export function keepWalks(walks: readonly Walk[]): Walk[] {
  const seen = new Set<string>()
  const out: Walk[] = []
  for (const w of [...walks].sort((a, b) => b.touched - a.touched)) {
    if (seen.has(w.tool)) continue
    seen.add(w.tool)
    out.push(w)
    if (out.length >= WALK_CAP) break
  }
  return out
}

// ── who the tool is, in a sentence ──────────────────────────────────────────

/** the tool's name as netti says it ("your tool" for the open door) */
export function toolWord(t: ConnectTool): string {
  return t.generic ? 'your tool' : t.name
}

/** the app the person actually opens, for a tool whose name says more ("Claude" for the desktop record) */
export function appWord(t: ConnectTool): string {
  if (t.id === 'claude-desktop') return 'Claude'
  return toolWord(t)
}

/** siblings a person often means instead: "not this one?" offers them on the wire step */
const SIBLINGS: Record<string, string[]> = {
  'claude-desktop': ['claude-code'],
  'claude-code': ['claude-desktop'],
  chatgpt: ['codex'],
  codex: ['chatgpt'],
  'gemini-cli': ['antigravity'],
  antigravity: ['gemini-cli'],
  cursor: ['vscode', 'windsurf'],
  vscode: ['cursor'],
  windsurf: ['cursor'],
}
export function siblingsOf(id: string): ConnectTool[] {
  return (SIBLINGS[id] ?? []).map((s) => walkTool(s)).filter((t): t is ConnectTool => !!t)
}

/** the order netti suggests what to connect next in: the apps people live in, then the agents */
const PREFERRED = ['claude-desktop', 'chatgpt', 'claude-code', 'cursor', 'codex', 'gemini-cli', 'vscode', 'windsurf', 'notion', 'antigravity', 'zed', 'cline', 'continue']

/** up to `n` tools not yet connected, in the order most people reach for them */
export function suggestNext(have: Iterable<string>, n = 3): ConnectTool[] {
  const got = new Set(have)
  return PREFERRED.filter((id) => !got.has(id))
    .map((id) => walkTool(id))
    .filter((t): t is ConnectTool => !!t)
    .slice(0, n)
}

/** every tool the pick card offers, the suggested first */
export function pickable(have: Iterable<string>): ConnectTool[] {
  const got = new Set(have)
  const first = suggestNext(got, PREFERRED.length)
  const rest = CONNECT_TOOLS.filter((t) => !t.generic && !first.some((f) => f.id === t.id) && !got.has(t.id)).map(withPlugin)
  return [...first, ...rest]
}

// ── what the person pastes ──────────────────────────────────────────────────

/** what the copy box on the wire step holds, and what it is called */
export function pasteOf(t: ConnectTool): { text: string; kind: 'link' | 'block' | 'command'; label: string; where: string | null } {
  return { text: t.config, kind: t.paste, label: t.configLabel, where: t.where ?? null }
}

/** the one-press door (Cursor, VS Code), with the shared link inside */
export function deeplinkOf(t: ConnectTool): string | null {
  return t.deeplink ? t.deeplink(MCP_URL) : null
}

/** the tool's own page, when there is one to open beside the steps */
export function openHref(t: ConnectTool): string | null {
  return t.href && /^https:\/\//.test(t.href) ? t.href : null
}

/** the steps on the wire card: the tool's own, with "below" pointing at the box */
export function wireSteps(t: ConnectTool): string[] {
  return t.steps.map((s) => s.replace(/\bthe command below\b/, 'the command').replace(/\bbelow\b/g, 'here'))
}

/** the line under the live check, once a while has passed with no call */
export const SLOW_AFTER_MS = 75_000

// ── the ways in ─────────────────────────────────────────────────────────────

/** which ways in this tool has, the quickest first. A tool with a published plugin has that one way and
 *  no other (John, 2026-10-30: "no other option but just the plugin"); `npx` stays reachable by asking.
 *  `coach` only where the extension can set the app up (Claude) and the person can have the extension;
 *  `npx` for every tool its own command wires. */
export function waysFor(t: ConnectTool, opts: { coach?: boolean } = {}): WalkWay[] {
  if (pluginOf(t)) return ['plugin']
  const out: WalkWay[] = []
  if (wiresItself(t.id)) out.push('npx')
  if (opts.coach && COACHED.has(t.id)) out.push('coach')
  out.push('hand')
  return out
}

/** the apps the browser extension can set up on their own page (ChatGPT has its plugin now) */
export const COACHED = new Set(['claude-desktop'])

/** the published plugin's page, for a tool that takes innernet as one */
export function pluginOf(t: Pick<ConnectTool, 'plugin'>): string | null {
  return t.plugin && /^https:\/\//.test(t.plugin) ? t.plugin : null
}

/** the command that wires every ai tool on a machine */
export const NPX_COMMAND = 'npx innernet'

/** One step of the by-hand way, as the card shows it: the words, the real screen for it when there is
 *  one, and what it hands over (the link, the block, the command, or the one-press door), only on the
 *  step that needs it. */
export interface HandStep {
  text: string
  shot: GuideShot | null
  paste: 'link' | 'block' | 'command' | null
  deeplink: string | null
}

const PASTE_WORDS = /\b(paste|link|url|block|config|command|run)\b/

export function handSteps(t: ConnectTool): HandStep[] {
  const words = wireSteps(t)
  const shots = t.guide?.mcp ?? []
  const door = deeplinkOf(t)
  // the thing to paste goes on the step that asks for it, once
  let pasteAt = words.findIndex((w) => PASTE_WORDS.test(w))
  if (door) pasteAt = -1
  if (pasteAt < 0 && !door) pasteAt = 0
  return words.map((text, i) => ({
    text,
    shot: shots.find((sh) => sh.step === i + 1) ?? null,
    paste: i === pasteAt ? t.paste : null,
    deeplink: door && i === 0 ? door : null,
  }))
}

/** the machines `npx innernet` has wired (a key each), and whether it wired this tool. A connection made
 *  through it is known as `key:<key id>:<tool id>` (lib/connect/match keyedClient). */
export function npxSeen(conns: readonly { client_id: string | null; tool_id: string }[], toolId: string): { machines: number; wired: boolean } {
  const keys = new Set<string>()
  let wired = false
  for (const c of conns) {
    const m = /^key:([^:]+):(.+)$/.exec(c.client_id ?? '')
    if (!m) continue
    keys.add(m[1])
    if (m[2] === toolId || c.tool_id === toolId) wired = true
  }
  return { machines: keys.size, wired }
}

// ── what netti says ─────────────────────────────────────────────────────────
//
// One line per moment, in netti's voice: lowercase (a proper noun keeps its case),
// direct, never an em dash (law_copy_direct). Each says what this IS, what happens
// NEXT, or what just HAPPENED.

export interface Said {
  text: string
  /** the one to three things the person might say next (ChatReplies) */
  replies?: string[]
}

/** the first thing netti asks of a new connection */
export function lineName(t: ConnectTool, suggested: string): Said {
  return {
    text: `let's connect ${appWord(t)}. what would you like to call this connection? the name shows wherever it reads or writes your memory.`,
    replies: [suggested],
  }
}

/** how to wire it, asked only when there is more than one way */
export function lineWay(t: ConnectTool, ways: readonly WalkWay[], opts: { npxBefore?: boolean; extension?: boolean } = {}): Said {
  const app = appWord(t)
  if (ways.includes('npx')) {
    const before = opts.npxBefore ? ` you've run it on a machine before; once more here picks ${app} up too.` : ''
    return {
      text: `the quickest way is one command in a terminal on this machine. it signs you in with your browser and wires ${app}, and every other ai tool it finds.${before}`,
      replies: ['i’d rather do it by hand', 'i don’t have node'],
    }
  }
  if (ways.includes('coach')) {
    return {
      text: opts.extension
        ? `the innernet extension can set ${app} up for you on its own page, or i can walk you through it one step at a time.`
        : `i can walk you through it one step at a time. or add the innernet extension to your browser and it sets ${app} up for you.`,
    }
  }
  return { text: `a few small moves in ${app}, one at a time.` }
}

/** one step of the by-hand way, said as an instruction */
export function lineHandStep(t: ConnectTool, i: number, n: number, text: string): Said {
  const lead = n === 1 ? '' : i === 0 ? 'first, ' : i === n - 1 ? 'last, ' : 'next, '
  const replies = i === 0 ? ['i can’t find it', 'which plan do i need?'] : ['it isn’t showing up', 'go back a step']
  return { text: `${lead}${text}.`.replace(/\.\.$/, '.'), replies }
}

/** every step done and no call yet */
export function lineListening(t: ConnectTool): Said {
  return {
    text: `that's all of it. once ${appWord(t)} has signed in, open a new chat and say hi. i'll hear it the moment it calls.`,
    replies: ['it isn’t showing up', 'skip ahead'],
  }
}

export function lineCoach(t: ConnectTool, ok: boolean): Said {
  return ok
    ? { text: `${appWord(t)} is open in a new tab. the card in its corner shows each step and fills the link in for you. i'm listening here.`, replies: ['it isn’t showing up', 'walk me through it'] }
    : { text: 'the extension did not answer just now, so let me walk you through it instead.' }
}

/** the plugin's one press, and what happens on the other side of it */
export function linePlugin(t: ConnectTool): Said {
  const terminal = wiresItself(t.id) ? ['i’d rather use the terminal'] : []
  return {
    text: t.id === 'chatgpt'
      ? `one press opens the innernet plugin in ChatGPT. press Connect there and sign in, and i'll hear it the moment it calls.`
      : `one press opens the innernet plugin. press Connect and sign in, and ${appWord(t)} has it from then on. i'll hear it the moment it calls.`,
    replies: ['it isn’t showing up', ...terminal],
  }
}

export function lineNpx(t: ConnectTool): Said {
  return {
    text: `run it in any terminal on this machine. your browser opens to sign you in, and i'll hear ${appWord(t)} the moment it calls.`,
    replies: ['i’d rather do it by hand', 'i don’t have node'],
  }
}

export function lineStart(t: ConnectTool, opts: { resumed?: boolean; connected?: boolean } = {}): Said {
  const app = appWord(t)
  if (opts.connected) {
    return { text: `${toolWord(t)} is already reading your memory. let's finish what makes it useful.`, replies: ['skip to the end', 'what else can i connect?'] }
  }
  if (opts.resumed) {
    return { text: `picking ${toolWord(t)} up where we left it.`, replies: ['it isn’t showing up', 'start over'] }
  }
  if (t.generic) {
    return {
      text: 'any app that speaks mcp can hold a connection. paste the link into its mcp or connector settings, sign in when your browser opens, and i’ll hear it arrive.',
      replies: ['what is mcp?', 'it has no mcp setting'],
    }
  }
  if (pluginOf(t)) {
    return { text: `let's bring ${app} in. it's one press: the innernet plugin, then you sign in. i'll be listening.`, replies: ['it isn’t showing up', 'which plan do i need?'] }
  }
  if (t.paste === 'command') {
    return { text: `let's bring ${app} in. it's one command in a terminal, and i'll know the moment it calls.`, replies: ['i don’t have node', 'show me where'] }
  }
  if (t.deeplink) {
    return { text: `let's bring ${app} in. one press opens it with innernet filled in, then you sign in. i'll be listening.`, replies: ['the button did nothing', 'show me where'] }
  }
  return {
    text: `let's bring ${app} in. ${t.steps.length} quick moves in ${app}, then i'll hear it the moment it calls.`,
    replies: ['show me where', 'which plan do i need?'],
  }
}

export function lineArrived(t: ConnectTool, label: string): Said {
  return {
    text: `${toolWord(t)} just called. **${label}** is reading your memory now.`,
  }
}

export function lineInstructions(t: ConnectTool): Said {
  if (selfWire(t.id)?.run.length || t.paste === 'command') {
    return {
      text: `the standing instruction tells ${toolWord(t)} when to reach for your memory. in a project folder, \`npx innernet\` writes it into that repo's AGENTS.md for you. anywhere else it goes in ${t.promptWhere}.`,
      replies: ['why does it need this?', 'skip this'],
    }
  }
  return {
    text: `next, the standing instruction, so ${appWord(t)} reaches for your memory without being asked. it goes in ${t.promptWhere}.`,
    replies: ['why does it need this?', 'skip this'],
  }
}

export function lineListenInstructions(t: ConnectTool): Said {
  return {
    text: `open a new chat in ${appWord(t)} and say hi. i'll watch for it to reach for your memory on its own.`,
    replies: ['it didn’t reach for it', 'skip the check'],
  }
}

export function lineHeard(t: ConnectTool): Said {
  return { text: `there it is. ${toolWord(t)} reached for your memory on its own.` }
}

export function lineTrust(t: ConnectTool): Said {
  return {
    text: `${appWord(t)} asks before every call to innernet unless you let it. two moves, and it stops asking.`,
    replies: ['is that safe?', 'skip this'],
  }
}

export function lineImport(t: ConnectTool): Said {
  return {
    text: `${appWord(t)} already knows things about you. bring them in once and every tool you connect starts from them.`,
    replies: ['what does it bring?', 'later'],
  }
}

export function lineDone(t: ConnectTool, count: number, next: ConnectTool[]): Said {
  const many = count > 1 ? `that's ${count} tools sharing one memory now.` : 'your memory travels with it now.'
  const first = next[0]
  return {
    text: `${toolWord(t)} is in. ${many} what you tell one, the others will know.${first ? ` want to bring ${toolWord(first)} in too?` : ''}`,
    replies: [...next.slice(0, 2).map((n) => `connect ${toolWord(n)}`), 'try it out'].slice(0, 3),
  }
}

export function linePick(count: number): Said {
  return {
    text: count
      ? 'what would you like to connect next? the ones most people reach for are first, or just tell me what it is.'
      : 'what would you like to connect? pick one, or tell me what it is and i’ll find the way in.',
  }
}

/** a tool asked for that is already connected: another one, or finish the one there is? */
export function lineAlready(t: ConnectTool, label: string, when: string | null): Said {
  return {
    text: `${toolWord(t)} is already connected: **${label}**${when ? ` last read your memory ${when}` : ' is waiting for its first call'}. is this another machine, or shall we finish setting that one up?`,
    replies: [`another ${toolWord(t)}`, `finish ${toolWord(t)}`],
  }
}

export function lineUnknown(what: string): Said {
  return {
    text: `i don't have ${what} written down, but anything that speaks mcp can hold a connection. if it has an mcp or connector setting, the link goes there.`,
    replies: ['it has no mcp setting', 'what is mcp?'],
  }
}

/** what to do when nothing has called yet, for this tool, as a short list */
export function troubleFor(t: ConnectTool): string[] {
  const own = TROUBLE[t.id] ?? []
  const wire = selfWire(t.id)
  const out = [
    ...own,
    ...(own.length || !t.where ? [] : [`the link goes in ${t.where}.`]),
    ...(own.length || !wire?.signIn ? [] : [wire.signIn]),
    'sign in when the innernet tab opens. if no tab opened, your browser may have held back the pop-up.',
    `open a new chat or session afterwards. one that was already open still has its old tools.`,
  ]
  const caveat = caveatOf(t)
  if (caveat) out.push(caveat)
  return out.slice(0, 5)
}

const TROUBLE: Record<string, string[]> = {
  'claude-desktop': [
    'connectors live under Customize → Connectors now. the old Settings → Connectors page only points there.',
    'after Add, press Connect on innernet. that is the press that opens the sign in.',
  ],
  chatgpt: [
    'on the innernet plugin page, press Connect. that is the press that opens the sign in.',
    'it is added on chatgpt.com in a browser once, and the phone app has it from then on. type @innernet in a chat to pick it.',
  ],
  codex: [
    'the plugin is added once on your ChatGPT account, and Codex has it from then on. start a new thread to see it.',
    'rather the terminal? `npx innernet` wires Codex too, and signs you in with your browser.',
  ],
  cursor: [
    'if the button did not open Cursor, paste the block into ~/.cursor/mcp.json by hand.',
    'then in Settings → Tools & MCP, find innernet and press the sign in beside it.',
  ],
  'claude-code': [
    'run it in a terminal on the same machine Claude Code runs on.',
    'if npx is not found, node is not installed yet. nodejs.org has it, then run the command again.',
    'restart Claude Code after, and type /mcp to see innernet there.',
  ],
  vscode: [
    'if the button did not open VS Code, add it from the command palette: MCP: Add Server, then HTTP, then the link.',
    'say yes when VS Code asks to sign in the first time a tool runs.',
  ],
  notion: [
    'custom MCP connections live in a Notion custom agent: its tools, then add a connection, then the link.',
  ],
}

/** what a plan or an admin has to say about this tool, or that any plan will do */
/** the plan and admin limits for this tool as it is walked: its plugin's own when it goes in as one */
function caveatOf(t: ConnectTool): string | undefined {
  return (pluginOf(t) ? PLUGIN_CONNECT[t.id]?.caveat : undefined) ?? CLIENT_CAVEATS[t.id]
}

export function planLine(t: ConnectTool): string {
  const caveat = caveatOf(t)
  return caveat ?? `any ${appWord(t)} that can add an mcp connector works. nothing else is needed on innernet's side.`
}

export const WHY_INSTRUCTION =
  'an ai only reaches for a tool when something tells it to. the standing instruction is that something: it says when to load what you are working on, and when to keep what it learns. without it, innernet waits to be asked.'

export const WHAT_IS_MCP =
  'mcp is the open way ais reach tools. innernet speaks it, so any app with an mcp or connector setting can hold your memory. you sign in with your browser; no key is ever pasted into a chat.'

export const IS_IT_SAFE =
  'it is. the tool can only read and write your innernet, through the doors you see in its permissions, and you can let any connection go from connections at any time. letting it run without asking just saves you a press on every call.'

export const NO_MCP =
  'then it can’t hold innernet directly yet. what works today: copy your memory into it with an export from innernet (settings → data), or use one of the apps above, and they will share what you keep.'

export const NO_NODE =
  'npx comes with node. install it from nodejs.org (the LTS one), open a new terminal, and run the command again. or connect Claude instead: it needs nothing installed.'

export const WHAT_IMPORT_BRINGS =
  'what the app already knows about you: who you are, how you work, what you are building. it lands as facts in your memory, and you see each one before it stays.'

export const LATER_LINE = 'all right. it waits in the strip at the top whenever you want to pick it up.'

/** said before the walk's own words when they answer a longer question only in part */
export const PARTIAL_LEAD =
  'i can only answer part of that from here for now. here is the closest i have; the connect docs have the rest.'

export const PREVIEW_FALLBACK =
  'from here i can only walk you through connecting for now. tell me which tool, or tap one of these. the connect docs have every way in too.'

export function lineStatus(rows: { name: string; state: string }[]): string {
  if (!rows.length) return 'nothing is connected yet. pick one and i’ll walk you through it.'
  return `here is where things stand: ${rows.map((r) => `${r.name}, ${r.state}`).join(' · ')}.`
}

/** the ask to paste into a freshly connected tool, to see it work */
export function tryItAsk(t: ConnectTool): string {
  return t.paste === 'command' || selfWire(t.id)?.run.length
    ? 'what do you know about me and what i am working on? check innernet.'
    : 'what do you know about me? check innernet.'
}

// ── what a typed line asks of the walk ─────────────────────────────────────

export type Intent =
  | { kind: 'tool'; tool: string; another?: boolean }
  | { kind: 'finish'; tool: string }
  | { kind: 'rename'; label: string }
  | { kind: 'done' }
  | { kind: 'skip' }
  | { kind: 'later' }
  | { kind: 'stuck' }
  | { kind: 'where' }
  | { kind: 'another' }
  | { kind: 'status' }
  | { kind: 'why' }
  | { kind: 'mcp' }
  | { kind: 'safe' }
  | { kind: 'plan' }
  | { kind: 'nomcp' }
  | { kind: 'nonode' }
  | { kind: 'imports' }
  | { kind: 'try' }
  | { kind: 'end' }
  | { kind: 'way'; way: WalkWay }
  | { kind: 'back' }
  | { kind: 'restart' }

const has = (s: string, re: RegExp) => re.test(s)

/** what a typed (or tapped) line asks of the walk, or null when it is a question for netti itself.
 *  `walking` says a walk is open, which lets short words ("done", "skip") mean the walk's step. */
export function readIntent(raw: string, walking: boolean): Intent | null {
  const s = (raw ?? '').toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim()
  if (!s) return null
  const words = connectTokens(s)

  const rename = /^(?:call|name|rename)\s+it\s+(.{1,80})$/i.exec((raw ?? '').trim())
  if (rename) return { kind: 'rename', label: rename[1].trim().replace(/^["'“]|["'”.]+$/g, '') }

  // the questions with one plain answer, before any tool name (they often carry one)
  if (has(s, /\b(no|without|doesn't have an?|does not have an?|has no)\b.*\bmcp\b/)) return { kind: 'nomcp' }
  if (has(s, /\b(no|don't have|do not have|without|install)\b.*\bnode\b|\bnpx\b.*\b(not found|missing|command not found)\b/)) return { kind: 'nonode' }
  if (has(s, /\bwhat('s| is| are)?\b.*\bmcp\b|\bmcp\b.*\bmean/)) return { kind: 'mcp' }
  if (has(s, /\b(safe|secure|risk|risky|trust it|privacy|private)\b/)) return { kind: 'safe' }
  if (has(s, /\b(plan|free|paid|pro|plus|team|enterprise|business|admin|subscription)\b/) && has(s, /\b(plan|need|work|which|do i|on|admin)\b/)) return { kind: 'plan' }
  if (has(s, /\bwhat does it bring\b|\bwhat (gets|will be) (imported|brought)\b/)) return { kind: 'imports' }
  if (walking && has(s, /\bwhy\b|\bwhat (is|does) (this|it|the instruction)\b/)) return { kind: 'why' }

  // stuck: before a tool name, because "it isn't showing up in claude" names one
  if (
    walking &&
    has(s, /\b(not|isn't|isnt|doesn't|doesnt|didn't|didnt|won't|wont|can't|cant|cannot|nothing|no)\b.*\b(work|working|show|showing|appear|appearing|happen|happening|connect|connecting|find|see|open|load|call|reach)/) ||
    (walking && has(s, /\b(stuck|error|broken|failed|fails|help|lost|confused|problem|issue|did nothing|nothing happened)\b/))
  ) return { kind: 'stuck' }

  if (walking && has(s, /\b(show me|where( is| do| does)?|screenshot|picture|which (button|screen|menu)|can't find)\b/)) return { kind: 'where' }

  // the ways in, in the person's words
  if (walking && has(s, /\b(by hand|myself|manually|walk me through|step by step|without the (command|terminal|extension))\b/)) return { kind: 'way', way: 'hand' }
  if (walking && has(s, /\b(the extension|do it for me|set it up for me|add the extension|install the extension)\b/)) return { kind: 'way', way: 'coach' }
  if (walking && has(s, /^(npx|the command|one command|use the terminal|run the command)\b|\brather use the terminal\b/)) return { kind: 'way', way: 'npx' }
  if (walking && has(s, /^(the plugin|use the plugin|add the plugin|add the innernet plugin|open the plugin)\b/)) return { kind: 'way', way: 'plugin' }
  if (walking && has(s, /^(go back|back|previous|the step before)\b/)) return { kind: 'back' }
  if (walking && has(s, /^(start over|start again|restart|from the top)\b/)) return { kind: 'restart' }

  // a tool named, as an ask to connect it ("connect cursor", "i use chatgpt", "actually claude code", "cursor")
  const named = namedClient(s)
  if (named && has(s, /\b(finish|set it up|setting it up|the rest)\b/)) return { kind: 'finish', tool: named.tool.id }
  if (named && has(s, /\banother\b|\bsecond\b|\bone more\b/)) return { kind: 'tool', tool: named.tool.id, another: true }
  if (named) {
    const lead = /^(connect|add|set ?up|setup|wire|install|use|try|now|next|and|also|actually|instead|what about|how about|i use|i'm on|im on|i have|with|let's do|lets do|do)\b/.test(s)
    const bare = words.length <= 3
    if (lead || bare || has(s, /\b(instead|too|as well|next)\b/)) return { kind: 'tool', tool: named.tool.id }
  }

  if (has(s, /\b(another|something else|more tools?|connect more|add more|what else|next one|other tools?|add one)\b/)) return { kind: 'another' }
  if (has(s, /\b(what's|what is|whats)\b.*\bconnected\b|\b(my|which) connections\b|\bstatus\b|\bwhere (do )?things stand\b/)) return { kind: 'status' }
  if (has(s, /\b(try it|test it|see it work|how do i know|does it work|check it works)\b/)) return { kind: 'try' }

  if (!walking) return null
  if (has(s, /^(later|not now|stop|pause|another time|i'll do it later|maybe later)\b/)) return { kind: 'later' }
  if (has(s, /^(skip to the end|finish|wrap up|that's all|all done|i'm done|im done)\b/)) return { kind: 'end' }
  if (has(s, /^(skip|skip (this|it|that|the check|ahead)|pass|no thanks|nah)\b/)) return { kind: 'skip' }
  if (has(s, /^(done|did it|i did|pasted|added|connected|ok|okay|yes|yep|next|got it|it's in|its in|finished|ready)\b/)) return { kind: 'done' }
  return null
}

// ── the line netti itself reads, when a question goes past the words above ──

/** one plain line for netti's context, built only from known ids and fixed words, so nothing the
 *  person typed can ride into netti's instructions through it */
export function connectContextLine(c: { tool?: unknown; step?: unknown; state?: unknown } | null | undefined): string | null {
  if (!c || typeof c !== 'object') return null
  const tool = typeof c.tool === 'string' ? walkTool(c.tool) : null
  if (!tool) return null
  const step = WALK_STEPS.includes(c.step as WalkStep) ? (c.step as WalkStep) : 'wire'
  const state = c.state === 'live' || c.state === 'waiting' ? c.state : 'waiting'
  const where: Record<WalkStep, string> = {
    name: `naming the connection for ${tool.name}`,
    wire: pluginOf(tool) ? `adding the innernet plugin to ${tool.name} (one press opens its page, then Connect)` : `adding innernet to ${tool.name} (${tool.where ?? 'its mcp settings'})`,
    instructions: `pasting the standing instruction into ${tool.promptWhere}`,
    trust: `letting ${tool.name} call innernet without asking each time`,
    import: `bringing what ${tool.name} knows about them into innernet`,
    done: `finished connecting ${tool.name}`,
  }
  const heard = state === 'live' ? `${tool.name} has called innernet` : `${tool.name} has not called innernet yet`
  return `they are connecting ${tool.name} in your rail right now, step: ${where[step]}. ${heard}. the steps are already on their screen in your rail; answer the question they ask about it plainly and briefly, and only repeat a step if they ask for it.`
}
